/**
 * PublicMenu — Menú digital para clientes (acceso por QR)
 *
 * Mejoras:
 *  • Fotos de productos con fallback elegante
 *  • Notas por ítem al agregar al carrito
 *  • Nombre y logo del negocio desde configuración
 *  • Seguimiento del pedido en tiempo real después de ordenar
 */

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getProducts } from '../services/ProductService';
import { getCategories } from '../services/CategoryService';
import { getTable } from '../services/TableService';
import { api } from '../utils/api';
import { formatCurrency } from '../utils/format-currency';
import { safeArray } from '../utils/safe';
import { getBusinessConfig } from '../hooks/useBusinessConfig';
import {
  ShoppingBag, ChevronLeft, Plus, Minus, Trash2, CheckCircle2,
  UtensilsCrossed, MessageSquare, X, ChefHat, Clock, Truck, Star,
} from 'lucide-react';
import { toast } from 'react-toastify';
import FlavorSelectorModal from '../components/Flavors/FlavorSelectorModal';
import type { Product } from '../types/@types/product';

// ── Helpers ───────────────────────────────────────────────────────────────────

const API_BASE = (import.meta as any).env?.VITE_API_URL ?? '';

function productImageUrl(product: Product): string | null {
  if (!product.image_path) return null;
  const path = product.image_path.startsWith('http') ? product.image_path : `${API_BASE}/uploads/${product.image_path}`;
  return path;
}

// ── Order status tracking ──────────────────────────────────────────────────────

const STATUS_STEPS = [
  { key: 'WAITING',       label: 'Recibido',       icon: CheckCircle2, color: 'text-blue-400'    },
  { key: 'IN_PRODUCTION', label: 'En preparación', icon: ChefHat,      color: 'text-amber-400'   },
  { key: 'DONE',          label: '¡Listo!',         icon: Star,         color: 'text-emerald-400' },
  { key: 'DELIVERED',     label: 'Entregado',       icon: Truck,        color: 'text-violet-400'  },
];

function OrderTracking({ orderId, tableNumber, onNewOrder }: {
  orderId: number;
  tableNumber?: string | number;
  onNewOrder: () => void;
}) {
  const [status, setStatus] = useState('WAITING');

  // Poll every 6s
  useEffect(() => {
    const poll = async () => {
      try {
        const { data } = await api.get(`/public/order/${orderId}/status`);
        setStatus(data.status);
      } catch { /* ignore */ }
    };
    poll();
    const iv = setInterval(poll, 6000);
    return () => clearInterval(iv);
  }, [orderId]);

  const currentIdx = STATUS_STEPS.findIndex(s => s.key === status);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#0d1221] p-8 text-center text-white">
      {/* Animated check */}
      <div className="mb-8 flex h-28 w-28 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400">
        <CheckCircle2 size={72} className="animate-[pulse_2s_ease-in-out_infinite]" />
      </div>

      <h1 className="mb-1 text-3xl font-black bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">
        ¡Pedido #{orderId} enviado!
      </h1>
      <p className="text-slate-400 mb-10">Mesa {tableNumber} · Estamos preparando tu pedido</p>

      {/* Status steps */}
      <div className="w-full max-w-sm space-y-3 mb-12">
        {STATUS_STEPS.map((step, idx) => {
          const Icon = step.icon;
          const done = idx <= currentIdx;
          const active = idx === currentIdx;
          return (
            <div
              key={step.key}
              className={`flex items-center gap-4 rounded-2xl px-5 py-4 transition-all ${
                active ? 'bg-white/10 border border-white/20 scale-[1.02]' :
                done   ? 'bg-white/5 border border-white/10' :
                         'opacity-40'
              }`}
            >
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                done ? 'bg-emerald-500/20' : 'bg-white/5'
              }`}>
                <Icon size={20} className={done ? step.color : 'text-slate-600'} />
              </div>
              <span className={`font-semibold ${done ? 'text-white' : 'text-slate-600'}`}>{step.label}</span>
              {active && (
                <span className="ml-auto flex h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              )}
              {done && idx < currentIdx && (
                <CheckCircle2 size={16} className="ml-auto text-emerald-400" />
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={onNewOrder}
        className="rounded-full bg-indigo-600 px-8 py-3 font-semibold text-white shadow-xl shadow-indigo-600/40 transition-all hover:bg-indigo-500 active:scale-95"
      >
        Hacer otro pedido
      </button>
    </div>
  );
}

// ── Note input for cart item ──────────────────────────────────────────────────

function NoteModal({ productName, currentNote, onSave, onClose }: {
  productName: string;
  currentNote: string;
  onSave: (note: string) => void;
  onClose: () => void;
}) {
  const [note, setNote] = useState(currentNote);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 p-4">
      <div className="w-full max-w-md rounded-t-3xl bg-[#1a2236] p-6 border-t border-white/10">
        <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
          <MessageSquare size={18} className="text-indigo-400" /> Nota para {productName}
        </h3>
        <p className="text-xs text-slate-500 mb-4">Ej: sin cebolla, extra salsa, poco picante...</p>
        <textarea
          ref={inputRef}
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={3}
          maxLength={200}
          placeholder="Sin cebolla, extra salsa..."
          className="w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500 resize-none"
        />
        <div className="flex gap-3 mt-4">
          <button onClick={onClose} className="flex-1 rounded-2xl border border-white/10 py-3 text-sm font-semibold text-slate-400 hover:bg-white/5">
            Cancelar
          </button>
          <button
            onClick={() => { onSave(note.trim()); onClose(); }}
            className="flex-1 rounded-2xl bg-indigo-600 py-3 text-sm font-bold text-white hover:bg-indigo-500"
          >
            Guardar nota
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Menu ─────────────────────────────────────────────────────────────────

interface CartItem {
  cartKey: string;
  product: Product;
  qty: number;
  flavors: any[];
  price: number;
  note: string;
}

const PublicMenu = () => {
  const { tableId } = useParams<{ tableId: string }>();
  const biz = getBusinessConfig();

  const [cart, setCart] = useState<CartItem[]>([]);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [flavorContext, setFlavorContext] = useState<{ product: Product } | null>(null);
  const [noteContext, setNoteContext] = useState<{ cartKey: string; product: Product; currentNote: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [placedOrderId, setPlacedOrderId] = useState<number | null>(null);

  const { data: table } = useQuery({
    queryKey: ['publicTable', tableId],
    queryFn: () => getTable(tableId!),
    enabled: !!tableId,
  });

  const { data: products, isLoading: loadingProducts } = useQuery({
    queryKey: ['publicProducts'],
    queryFn: getProducts,
    staleTime: 5 * 60_000,
  });

  const { data: categories } = useQuery({
    queryKey: ['publicCategories'],
    queryFn: getCategories,
    staleTime: 5 * 60_000,
  });

  const filteredProducts = useMemo(() => {
    const safeProducts = safeArray(products);
    if (!activeCategory) return safeProducts;
    return safeProducts.filter(p =>
      typeof p.category === 'string' ? p.category === activeCategory : (p.category as any)?.id === activeCategory
    );
  }, [products, activeCategory]);

  // ── Cart actions ────────────────────────────────────────────────────────────

  const addToCart = useCallback((product: Product, selectedFlavors: any[] = [], note = '') => {
    const cartKey = `${product.id}-${selectedFlavors.map(f => `${f.id}:${f.qty}`).join(',')}`;
    setCart(prev => {
      const safePrev = safeArray(prev);
      const existing = safePrev.find(i => i.cartKey === cartKey);
      if (existing) {
        return safePrev.map(i => i.cartKey === cartKey ? { ...i, qty: i.qty + 1 } : i);
      }
      const price = product.price + selectedFlavors.reduce((acc, f) => acc + (f.price_delta || 0) * f.qty, 0);
      return [...safePrev, { cartKey, product, qty: 1, flavors: selectedFlavors, price, note }];
    });
    toast.success(`${product.name} agregado`, { autoClose: 1200 });
  }, []);

  const handleProductClick = (product: Product) => {
    const hasFlavors = (product.maxFlavors && product.maxFlavors > 0) || product.flavors?.enabled;
    if (hasFlavors) {
      setFlavorContext({ product });
    } else {
      addToCart(product);
    }
  };

  const removeFromCart = (cartKey: string) => setCart(prev => {
    const safePrev = safeArray(prev);
    return safePrev.filter(i => i.cartKey !== cartKey);
  });

  const updateQty = (cartKey: string, delta: number) => {
    setCart(prev => {
      const safePrev = safeArray(prev);
      return safePrev.map(i => {
        if (i.cartKey !== cartKey) return i;
        const newQty = i.qty + delta;
        if (newQty <= 0) return null as any;
        return { ...i, qty: newQty };
      }).filter(Boolean as any);
    });
  };

  const setItemNote = (cartKey: string, note: string) => {
    setCart(prev => safeArray(prev).map(i => i.cartKey === cartKey ? { ...i, note } : i));
  };

  const safeCart = safeArray(cart);
  const cartTotal = safeCart.reduce((acc, i) => acc + i.price * i.qty, 0);
  const cartCount = safeCart.reduce((acc, i) => acc + i.qty, 0);

  // ── Submit ──────────────────────────────────────────────────────────────────

  const submitOrder = async () => {
    if (safeCart.length === 0) return;
    setIsSubmitting(true);
    try {
      const { data } = await api.post('/orders/self-order', {
        tableId,
        items: safeCart.map(i => ({
          product:  i.product.id,
          quantity: i.qty,
          notes:    i.note || undefined,
          flavors:  safeArray(i.flavors).map((f: any) => ({ flavorId: f.id, qty: f.qty })),
        })),
      });
      setPlacedOrderId(data.id);
      setCart([]);
      setIsCartOpen(false);
    } catch {
      toast.error('Error al enviar el pedido. Intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Order tracking screen ────────────────────────────────────────────────────

  if (placedOrderId) {
    return (
      <OrderTracking
        orderId={placedOrderId}
        tableNumber={table?.number ?? tableId}
        onNewOrder={() => setPlacedOrderId(null)}
      />
    );
  }

  // ── Business branding ─────────────────────────────────────────────────────

  const bizName = biz.name || 'Mi Restaurante';
  const bizLogo = biz.logoUrl;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#0d1221] text-slate-200">

      {/* ── Header ── */}
      <header className="sticky top-0 z-40 bg-[#0d1221]/90 backdrop-blur-xl border-b border-white/5 px-5 py-4">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <div className="flex items-center gap-3">
            {bizLogo ? (
              <img src={bizLogo} alt={bizName} className="h-12 w-12 rounded-2xl object-contain bg-white/5 p-1" />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-600 shadow-lg shadow-indigo-900/40 text-white">
                <UtensilsCrossed size={22} />
              </div>
            )}
            <div>
              <h1 className="text-lg font-black text-white tracking-tight">{bizName}</h1>
              <div className="flex items-center gap-2">
                <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                <p className="text-xs text-indigo-400 font-bold uppercase tracking-widest">
                  Mesa {table?.number ?? tableId}
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={() => setIsCartOpen(true)}
            className="relative flex h-12 w-12 items-center justify-center rounded-full bg-white/5 border border-white/10 transition-all hover:bg-white/10 active:scale-90"
          >
            <ShoppingBag size={22} />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 text-[11px] font-black text-white border-2 border-[#0d1221]">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* ── Categories ── */}
      <div className="sticky top-[76px] z-30 flex gap-2 overflow-x-auto bg-[#0d1221]/90 backdrop-blur-xl px-4 py-3 border-b border-white/5 no-scrollbar">
        <button
          onClick={() => setActiveCategory(null)}
          className={`flex-shrink-0 whitespace-nowrap rounded-full px-5 py-2 text-sm font-bold transition-all ${
            activeCategory === null
              ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/30'
              : 'bg-white/5 text-slate-400 hover:bg-white/10'
          }`}
        >
          Todo
        </button>
        {safeArray(categories).map((cat: any) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`flex-shrink-0 whitespace-nowrap rounded-full px-5 py-2 text-sm font-bold transition-all ${
              activeCategory === cat.id
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/30'
                : 'bg-white/5 text-slate-400 hover:bg-white/10'
            }`}
          >
            {cat.icon && <span className="mr-1">{cat.icon}</span>}{cat.name}
          </button>
        ))}
      </div>

      {/* ── Products ── */}
      <main className="px-4 py-6 pb-36 max-w-2xl mx-auto">
        {loadingProducts ? (
          <div className="flex h-64 flex-col items-center justify-center gap-6">
            <div className="relative h-14 w-14">
              <div className="h-14 w-14 rounded-full border-4 border-indigo-600/20" />
              <div className="absolute top-0 h-14 w-14 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent" />
            </div>
            <p className="text-slate-500 font-bold animate-pulse uppercase tracking-wider text-xs">Cargando menú...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredProducts.map(product => {
              const imgUrl = productImageUrl(product);
              const cartItem = cart.find(i => i.product.id === product.id);
              return (
                <div
                  key={product.id}
                  className="group relative overflow-hidden rounded-3xl bg-white/5 border border-white/10 transition-all hover:border-indigo-500/40"
                >
                  {/* Image */}
                  {imgUrl ? (
                    <div className="h-44 overflow-hidden">
                      <img
                        src={imgUrl}
                        alt={product.name}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </div>
                  ) : (
                    <div className="h-28 flex items-center justify-center bg-gradient-to-br from-indigo-900/30 to-slate-900/30">
                      <UtensilsCrossed size={36} className="text-indigo-800" />
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-5">
                    <h3 className="font-bold text-white text-lg tracking-tight leading-tight">{product.name}</h3>
                    {product.description && (
                      <p className="mt-1.5 text-sm text-slate-400 leading-relaxed line-clamp-2">{product.description}</p>
                    )}

                    <div className="mt-4 flex items-center justify-between gap-3">
                      <p className="text-2xl font-black text-white tracking-tighter">{formatCurrency(product.price)}</p>

                      {cartItem ? (
                        // Show qty controls if in cart
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQty(cartItem.cartKey, -1)}
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 border border-white/10 text-white hover:bg-rose-500/20 hover:text-rose-400 transition"
                          >
                            {cartItem.qty === 1 ? <Trash2 size={16} /> : <Minus size={16} />}
                          </button>
                          <span className="w-6 text-center font-black text-white">{cartItem.qty}</span>
                          <button
                            onClick={() => handleProductClick(product)}
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg transition hover:bg-indigo-500"
                          >
                            <Plus size={18} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleProductClick(product)}
                          className="flex items-center gap-2 rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-indigo-900/40 transition hover:bg-indigo-500 active:scale-95"
                        >
                          <Plus size={16} /> Agregar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredProducts.length === 0 && !loadingProducts && (
              <div className="flex h-48 flex-col items-center justify-center gap-3 text-slate-600">
                <UtensilsCrossed size={40} strokeWidth={1} />
                <p className="font-semibold">Sin productos en esta categoría</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* ── Floating Cart Bar ── */}
      {cartCount > 0 && !isCartOpen && (
        <div className="fixed bottom-8 left-1/2 z-40 -translate-x-1/2 w-[92%] max-w-md">
          <button
            onClick={() => setIsCartOpen(true)}
            className="flex w-full items-center justify-between rounded-[28px] bg-indigo-600 p-2 pl-6 text-white shadow-[0_20px_50px_rgba(79,70,229,0.5)] border border-indigo-500/50 transition-all active:scale-95"
          >
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/60 text-left">Carrito</p>
              <p className="font-black text-lg tracking-tight">{cartCount} ítem{cartCount !== 1 ? 's' : ''}</p>
            </div>
            <div className="flex items-center gap-4 bg-white/10 h-14 rounded-[22px] px-5 border border-white/10">
              <p className="text-xl font-black tracking-tighter">{formatCurrency(cartTotal)}</p>
              <ChevronLeft className="rotate-180" size={22} />
            </div>
          </button>
        </div>
      )}

      {/* ── Cart Sheet ── */}
      {isCartOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-t-[36px] bg-[#1a2236] p-6 pb-10 shadow-[0_-20px_60px_rgba(0,0,0,0.6)] border-t border-white/10 max-h-[90dvh] flex flex-col">
            <div className="mx-auto mb-5 h-1.5 w-14 rounded-full bg-slate-700/60" />

            <div className="flex items-center justify-between mb-6 shrink-0">
              <div>
                <h2 className="text-2xl font-black text-white tracking-tight">Tu Pedido</h2>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-0.5">
                  {cartCount} ítem{cartCount !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={() => setIsCartOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-white/5 border border-white/10 text-slate-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            {/* Items */}
            <div className="overflow-y-auto flex-1 pr-1 space-y-3 mb-5">
              {safeArray(cart).length === 0 ? (
                <div className="py-16 text-center">
                  <ShoppingBag size={36} className="mx-auto mb-3 text-slate-700" />
                  <p className="text-slate-500 font-bold uppercase tracking-wider text-sm">Carrito vacío</p>
                </div>
              ) : safeArray(cart).map(item => (
                <div key={item.cartKey} className="rounded-2xl bg-white/5 border border-white/5 p-4">
                  <div className="flex gap-4">
                    {/* Small image */}
                    {productImageUrl(item.product) ? (
                      <img
                        src={productImageUrl(item.product)!}
                        alt={item.product.name}
                        className="h-14 w-14 rounded-xl object-cover shrink-0"
                      />
                    ) : (
                      <div className="h-14 w-14 shrink-0 rounded-xl bg-indigo-900/30 flex items-center justify-center">
                        <UtensilsCrossed size={20} className="text-indigo-800" />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-bold text-white leading-snug">{item.product.name}</h4>
                        <p className="font-black text-indigo-400 shrink-0">{formatCurrency(item.price * item.qty)}</p>
                      </div>

                      {safeArray(item.flavors).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {safeArray(item.flavors).map((f: any) => (
                            <span key={f.id} className="px-2 py-0.5 rounded-lg bg-indigo-600/10 text-[10px] text-indigo-400 font-bold border border-indigo-500/20">
                              {f.name} ×{f.qty}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Note */}
                      {item.note ? (
                        <button
                          onClick={() => setNoteContext({ cartKey: item.cartKey, product: item.product, currentNote: item.note })}
                          className="mt-1.5 flex items-center gap-1 text-[11px] text-amber-400 hover:text-amber-300"
                        >
                          <MessageSquare size={10} /> {item.note}
                        </button>
                      ) : (
                        <button
                          onClick={() => setNoteContext({ cartKey: item.cartKey, product: item.product, currentNote: '' })}
                          className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-300"
                        >
                          <MessageSquare size={10} /> Añadir nota
                        </button>
                      )}

                      {/* Qty controls */}
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          onClick={() => updateQty(item.cartKey, -1)}
                          className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/5 border border-white/5 text-slate-300 hover:bg-rose-500/20 hover:text-rose-400 transition"
                        >
                          {item.qty === 1 ? <Trash2 size={14} /> : <Minus size={14} />}
                        </button>
                        <span className="w-5 text-center text-sm font-black text-white">{item.qty}</span>
                        <button
                          onClick={() => updateQty(item.cartKey, 1)}
                          className="flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-sm transition hover:bg-indigo-500"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Total + CTA */}
            <div className="shrink-0 space-y-4">
              <div className="flex items-center justify-between text-white">
                <span className="text-slate-400 font-bold uppercase tracking-widest text-xs">Total</span>
                <span className="text-2xl font-black tracking-tighter">{formatCurrency(cartTotal)}</span>
              </div>
              <button
                disabled={cart.length === 0 || isSubmitting}
                onClick={submitOrder}
                className="w-full rounded-2xl bg-indigo-600 py-4 font-black text-white shadow-2xl shadow-indigo-900/60 transition-all hover:bg-indigo-500 disabled:opacity-50 active:scale-[0.98]"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    <span className="uppercase tracking-widest text-sm">Enviando...</span>
                  </div>
                ) : (
                  <span className="uppercase tracking-[0.15em] text-sm">Confirmar pedido</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Flavor modal ── */}
      {flavorContext && (
        <FlavorSelectorModal
          open
          onClose={() => setFlavorContext(null)}
          product={flavorContext.product}
          onConfirm={payload => {
            addToCart(flavorContext.product, payload.flavors);
            setFlavorContext(null);
          }}
        />
      )}

      {/* ── Note modal ── */}
      {noteContext && (
        <NoteModal
          productName={noteContext.product.name}
          currentNote={noteContext.currentNote}
          onSave={note => setItemNote(noteContext.cartKey, note)}
          onClose={() => setNoteContext(null)}
        />
      )}
    </div>
  );
};

export default PublicMenu;
